// Консоль игры в window: заказ раунда для тестировщика и выбор языка.
//
// Игра ставит объект slot в window и печатает список команд. Тестировщик зовёт команды руками:
// своего экрана у них нет. Заказ раунда описан в ADR-32, выбор языка в ADR-37.
//
// Настройки меняют язык на ходу, см. ADR-26. Команда осталась тестовым запасным путём и
// перезагружает страницу: ей не нужно держать живую сцену и её состояние.

import { LOCALES, isLocale, writeLocale, type Locale } from '../i18n/locale';
import { translation } from '../i18n/text';

import { cheatCommands, cheatHelp, type CheatBoard, type CheatCommands } from './cheats';

/** Имя объекта консоли в window. */
const NAME: string = 'slot';

/** Команды выбора языка. */
export interface LocaleCommands {
  /** Все языки игры по именам. */
  readonly locales: readonly Locale[];

  /**
   * Называет язык игры, а с именем языка — сохраняет его и перезагружает страницу.
   *
   * @param next Имя языка. Незнакомое имя игра не берёт и остаётся на своём.
   * @returns Язык, на котором игра идёт.
   */
  locale(next?: string): Locale;
}

/** Всё, что игра кладёт в window. */
export type GameConsole = CheatCommands &
  LocaleCommands & {
    /** Печатает список команд. */
    help(): void;
  };

/** Список команд выбора языка для консоли браузера. */
export function localeHelp(name: string): string {
  return [
    'Язык игры:',
    `  ${name}.locale() — на каком языке идёт игра`,
    `  ${name}.locale('ru_RU') — сменить язык и перезагрузить страницу`,
    `  ${name}.locales — все языки игры`,
  ].join('\n');
}

/** Весь список команд консоли. */
export function consoleHelp(): string {
  return `${cheatHelp(NAME)}\n${localeHelp(NAME)}`;
}

/** Собирает команды выбора языка. */
export function localeCommands(): LocaleCommands {
  return {
    locales: LOCALES,
    locale: (next?: string): Locale => {
      const now: Locale = translation().locale;

      if (next === undefined)
        return now;

      if (!isLocale(next)) {
        console.warn(`Игра не знает язык ${next}. Все языки: ${NAME}.locales`);
        return now;
      }

      writeLocale(next);
      location.reload();

      return next;
    },
  };
}

declare global {
  interface Window {
    /** Консоль игры. Её ставит игровое поле на старте, см. game/console.ts. */
    slot?: GameConsole;
  }
}

/** Ставит консоль в window и печатает список команд. */
export function installConsole(game: CheatBoard): void {
  window.slot = {
    ...cheatCommands(game),
    ...localeCommands(),
    help: (): void => {
      console.info(consoleHelp());
    },
  };

  console.info(consoleHelp());
}

/** Убирает консоль из window: игры за ней больше нет. */
export function removeConsole(): void {
  delete window.slot;
}
